import { ReleaseHighlight, useReleaseHighlights } from "@/entities/release-highlight";
import { Button, Popover, PopoverContent, PopoverTrigger, Separated } from "@/shared/components";
import { formatDateRelative } from "@/shared/utils";
import { useMemo, useState } from "react";
import { useReleaseHighlightStore } from "../release-highlight-store.ts";
import { ReleaseHighlightDialog } from "./release-highlight-dialog.tsx";

export function ReleaseHighlightMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const [geopendBericht, setGeopendBericht] = useState<ReleaseHighlight | null>(null);
    const gelezenBerichtIds = useReleaseHighlightStore((state) => state.gelezenBerichtIds);
    const markAlsGelezen = useReleaseHighlightStore((state) => state.markAlsGelezen);
    const { data } = useReleaseHighlights();

    const berichten = useMemo(() => (data ? ReleaseHighlight.getVisiblePublishedMessages(data) : []), [data]);

    const heeftOngelezen = ReleaseHighlight.hasUnreadMessages(berichten, gelezenBerichtIds);

    const handleSelectBericht = (bericht: ReleaseHighlight) => {
        setIsOpen(false);
        setGeopendBericht(bericht);
        markAlsGelezen(String(bericht.id), berichten);
    };

    return (
        <>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <div className="relative inline-flex">
                    <PopoverTrigger asChild data-testid="release-highlight">
                        <Button icon="news" variant="tertiary" iconOnly title="Release hoogtepunten" aria-label="Release hoogtepunten" />
                    </PopoverTrigger>
                    {heeftOngelezen && (
                        <span
                            data-testid="release-highlight-ongelezen"
                            role="status"
                            aria-label="Er zijn ongelezen berichten"
                            className="bg-error-default pointer-events-none absolute top-1 right-1 size-2 rounded-full"
                        />
                    )}
                </div>
                <PopoverContent className="text-content-default w-96 p-0" align="end" data-testid="release-highlight">
                    <div className="border-default border-b px-4 py-3">
                        <h6 className="text-s text-content-emphasis leading-s font-bold">Release hoogtepunten</h6>
                    </div>
                    {berichten.length === 0 ? (
                        <p className="text-s text-content-subtle px-4 py-6">Er zijn nog geen berichten.</p>
                    ) : (
                        <ul className="flex flex-col">
                            <Separated>
                                {berichten.map((bericht) => {
                                    const isOngelezen = !ReleaseHighlight.isRead(bericht, gelezenBerichtIds);

                                    return (
                                        <li key={bericht.id}>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectBericht(bericht)}
                                                data-testid="release-highlight-item"
                                                data-ongelezen={isOngelezen}
                                                className="hover:bg-hover focus-visible:bg-hover relative flex w-full flex-col gap-1 py-3 pr-4 pl-5 text-left outline-hidden"
                                            >
                                                {isOngelezen && (
                                                    <span
                                                        data-testid="release-highlight-ongelezen-balk"
                                                        className="bg-info-default pointer-events-none absolute inset-y-0 left-0 w-1"
                                                    />
                                                )}
                                                <span className="flex items-baseline justify-between gap-2">
                                                    <span className="text-s text-content-emphasis leading-s font-bold">
                                                        {bericht.titel}
                                                    </span>
                                                    <span className="text-content-subtle text-xs">{formatDateRelative(bericht.datum)}</span>
                                                </span>
                                                <span className="text-s text-content-subtle leading-s">{bericht.korteOmschrijving}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </Separated>
                        </ul>
                    )}
                </PopoverContent>
            </Popover>

            <ReleaseHighlightDialog bericht={geopendBericht} onClose={() => setGeopendBericht(null)} />
        </>
    );
}
